import React, { useState, useMemo } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, X, LogIn, UserCircle } from 'lucide-react';
import Logo from '@/components/Logo';
import api from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

// Nav items: labels tuned to fit within the desktop header row without overlap.
const ALL_NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Church', shortLabel: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/leaflets', label: 'Leaflets' },
  { to: '/morning-prayer', label: 'Morning Prayer', shortLabel: 'Prayer' },
  { to: '/live', label: 'LIVE Service', shortLabel: 'LIVE' },
  { to: '/media', label: 'Media' },
  { to: '/donate', label: 'Donate' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings();

  // Only surface the Leaflets link when at least one published leaflet exists.
  // Shares the same query key as the footer so a single network call powers both.
  // Falls back to hidden on error so the header never shows a dead link.
  const { data: leaflets = [] } = useQuery({
    queryKey: ['pub-leaflets-footer'],
    queryFn: async () => (await api.get('/public/leaflets')).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const hasLeaflets = Array.isArray(leaflets) && leaflets.length > 0;

  // Filter nav items based on admin toggles + content presence.
  // useMemo keeps the array stable across renders so NavLink children don't thrash.
  const navItems = useMemo(
    () =>
      ALL_NAV_ITEMS.filter((item) => {
        if (item.to === '/donate') return settings.donate_page_visible !== false;
        if (item.to === '/leaflets') return hasLeaflets;
        return true;
      }),
    [settings.donate_page_visible, hasLeaflets],
  );

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b-2 border-moj-gold/30 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center gap-2 xl:gap-4 h-16 sm:h-20">
          {/* Logo (fixed max width so it never pushes the nav).
              min-w-0 is required so the wordmark can truncate on very small screens
              (<360px) rather than forcing the hamburger off the right edge. */}
          <Link to="/" data-testid="header-logo-link" className="flex-shrink min-w-0 overflow-hidden">
            <Logo size="md" />
          </Link>

          {/* Nav — center-flexed, uses the compact shortLabel in the desktop row so
              all nine items fit inside the 1280 px max-w-7xl container without
              overlapping the right-side Members Login button. The full `label`
              is still exposed via `aria-label` for screen readers and remains
              the long form used in the mobile dropdown below. */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-0.5 xl:gap-1 min-w-0" aria-label="Main navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                aria-label={item.label}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2 xl:px-3 py-1.5 rounded-full font-medium text-[13px] xl:text-sm transition-all ${
                    isActive
                      ? 'bg-moj-purple text-white shadow'
                      : 'text-moj-purple-deep hover:bg-moj-cream'
                  }`
                }
                end={item.to === '/'}
              >
                {item.shortLabel || item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right-side action — icon-only at lg, labeled from xl up so the full
              nine-item nav never collides with the login button at any width. */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            {user ? (
              <>
                <Link
                  to={user.status === 'approved' ? '/member/dashboard' : '/pending-approval'}
                  data-testid="header-dashboard-btn"
                  aria-label="My Dashboard"
                  className="btn-outline text-xs xl:text-sm py-1.5 px-2 xl:px-4"
                >
                  <UserCircle size={16} />
                  <span className="hidden xl:inline">My Dashboard</span>
                </Link>
                <button onClick={async () => { await logout(); navigate('/'); }} className="text-xs xl:text-sm text-moj-purple-deep hover:underline">
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="header-members-login-btn"
                aria-label="Members Login"
                className="btn-primary text-xs xl:text-sm py-1.5 px-2 xl:px-5"
              >
                <LogIn size={16} />
                <span className="hidden xl:inline">Members Login</span>
              </Link>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden ml-auto p-2 rounded-md text-moj-purple-deep hover:bg-moj-cream flex-shrink-0"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-moj-gold/20 py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg font-medium ${isActive ? 'bg-moj-purple text-white' : 'text-moj-purple-deep hover:bg-moj-cream'}`
                }
                end={item.to === '/'}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-moj-gold/20 px-4">
              {user ? (
                <Link onClick={() => setOpen(false)} to={user.status === 'approved' ? '/member/dashboard' : '/pending-approval'} className="btn-primary w-full justify-center">
                  My Dashboard
                </Link>
              ) : (
                <Link onClick={() => setOpen(false)} to="/login" className="btn-primary w-full justify-center">
                  <LogIn size={18}/> Members Login
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
